import {
  TEntityResponse,
  TEntityListResponse,
  TCreateDeviceReqBody,
  TDeviceResBody,
  TDeviceSummaryResBody,
  TUpdateDeviceReqBody,
  TDeviceActionSummaryResBody,
  TDeviceErrorSummaryResBody,
  TDeviceMetricSummaryResBody,
  TDeviceStateSummaryResBody,
  TDeviceFrameSummaryResBody,
  TDeviceConfigSummaryResBody,
  TDeviceImportResBody,
  TDeviceExportResBody,
} from '@poseidon-city/shared';

import { TAPIClient, TSSEHandler } from '../client';

type TChartData = {
  values: number[];
  labels: string[];
};

type TActivateDeviceBody = {
  f_cnt_up?: number;
  n_f_cnt_down?: number;
  a_f_cnt_down?: number;
};

type TDevicesClient = {
  create(body: TCreateDeviceReqBody): Promise<TEntityResponse<'device', TDeviceResBody> | undefined>;
  update(id: string, body: TUpdateDeviceReqBody): Promise<TEntityResponse<'device', TDeviceResBody> | undefined>;
  delete(id: string): Promise<void>;
  get(id: string): Promise<TEntityResponse<'device', TDeviceResBody> | undefined>;
  list(
    limit: number,
    page: number,
    filter?: string
  ): Promise<TEntityListResponse<'device', TDeviceSummaryResBody> | undefined>;
  list_by_status(
    limit: number,
    page: number,
    status: 'OFFLINE' | 'ONLINE' | 'ERROR' | 'NEVER_SEEN',
    filter?: string
  ): Promise<TEntityListResponse<'device', TDeviceSummaryResBody> | undefined>;
  list_area_devices(areaId: string): Promise<TEntityListResponse<'device', TDeviceSummaryResBody> | undefined>;
  list_actions(
    limit: number,
    page: number,
    deviceId: string
  ): Promise<TEntityListResponse<'device-action', TDeviceActionSummaryResBody> | undefined>;
  list_errors(
    limit: number,
    page: number,
    deviceId: string
  ): Promise<TEntityListResponse<'device-error', TDeviceErrorSummaryResBody> | undefined>;
  list_metrics(
    limit: number,
    page: number,
    deviceId: string
  ): Promise<TEntityListResponse<'device-metric', TDeviceMetricSummaryResBody> | undefined>;
  get_metrics_for_text(
    deviceId: string,
    metricId: string
  ): Promise<TEntityListResponse<'device-metric', TDeviceMetricSummaryResBody> | undefined>;
  get_metrics_for_graph(
    deviceId: string,
    metricKey: string,
    timeFrame: 'day' | 'week' | 'month' | 'year' | undefined
  ): Promise<any | undefined>;
  get_states_for_graph(
    deviceId: string,
    stateKey: string,
    timeFrame: 'day' | 'week' | 'month' | 'year' | undefined
  ): Promise<any | undefined>;
  list_states(
    limit: number,
    page: number,
    deviceId: string
  ): Promise<TEntityListResponse<'device-state', TDeviceStateSummaryResBody> | undefined>;
  get_frames_for_device(
    limit: number,
    page: number,
    deviceId: string,
    sort: 'asc' | 'desc'
  ): Promise<TEntityListResponse<'device-frame', TDeviceFrameSummaryResBody> | undefined>;
  get_last_state(
    limit: number,
    page: number,
    deviceId: string,
    stateTypeId: string,
    sort: 'asc' | 'desc'
  ): Promise<TEntityListResponse<'device-state', TDeviceStateSummaryResBody> | undefined>;
  get_last_config(
    limit: number,
    page: number,
    deviceId: string,
    configTypeId: string,
    sort: 'asc' | 'desc',
    filter: 'REPORTED' | 'REQUESTED'
  ): Promise<TEntityListResponse<'device-config', TDeviceConfigSummaryResBody> | undefined>;
  get_available_commands(id: string): Promise<TEntityListResponse<'device-command', any> | undefined>;
  execute_command(id: string, command: any): Promise<void>;
  set_group(id: string, body: { subgroup_id: string | null }): Promise<void>;
  metrics_chart_data(
    key: 'power',
    timeFrame: 'day' | 'week' | 'month' | 'year',
    type: 'group' | 'company' | 'area' | 'all',
    value: string
  ): Promise<TEntityResponse<'chart-data', TChartData> | undefined>;
  activate(id: string, body?: TActivateDeviceBody): Promise<void>;
  reactivate(id: string): Promise<void>;
  import_from_google_sheet(
    sheetTitle: string
  ): Promise<TEntityResponse<'device-import', TDeviceImportResBody> | undefined>;
  export_devices(): Promise<TEntityResponse<'device-export', TDeviceExportResBody> | undefined>;
  upload_import_xml(file: File): Promise<TEntityResponse<'device-import', TDeviceImportResBody> | undefined>;
  process_device_import(importId: string): Promise<TEntityResponse<'device-import', TDeviceImportResBody> | undefined>;
  confirm_device_import(importId: string): Promise<TEntityResponse<'device-import', TDeviceImportResBody> | undefined>;
  list_exports(limit: number, page: number): Promise<TEntityListResponse<'device-export', any> | undefined>;
  list_imports(limit: number, page: number): Promise<TEntityListResponse<'device-import', any> | undefined>;
  get_import(id: string): Promise<TEntityResponse<'device-import', TDeviceImportResBody> | undefined>;
  get_export(id: string): Promise<TEntityResponse<'device-export', TDeviceExportResBody> | undefined>;
  stream_export(id: string, onEvent: TSSEHandler, signal: AbortSignal): Promise<void>;
  stream_import(id: string, onEvent: TSSEHandler, signal: AbortSignal): Promise<void>;
  delete_import(id: string): Promise<void>;
  delete_export(id: string): Promise<void>;
};

function DevicesClient(apiClient: TAPIClient): TDevicesClient {
  return {
    async create(body: TCreateDeviceReqBody): Promise<TEntityResponse<'device', TDeviceResBody> | undefined> {
      const data = await apiClient.post<TCreateDeviceReqBody, TEntityResponse<'device', TDeviceResBody>>(
        `/devices`,
        body
      );

      if (data && data.type !== 'device') {
        throw new Error('Invalid entity type received, expected: device, received: ' + data.type);
      }

      return data;
    },

    async update(
      id: string,
      body: TUpdateDeviceReqBody
    ): Promise<TEntityResponse<'device', TDeviceResBody> | undefined> {
      const data = await apiClient.put<TUpdateDeviceReqBody, TEntityResponse<'device', TDeviceResBody>>(
        `/devices/${id}`,
        body
      );

      if (data && data.type !== 'device') {
        throw new Error('Invalid entity type received, expected: device, received: ' + data.type);
      }

      return data;
    },

    async delete(id: string): Promise<void> {
      return await apiClient.delete(`/devices/${id}`);
    },

    async get(id: string): Promise<TEntityResponse<'device', TDeviceResBody> | undefined> {
      const data = await apiClient.get<TEntityResponse<'device', TDeviceResBody>>(`/devices/${id}`);

      if (data && data.type !== 'device') {
        throw new Error('Invalid entity type received, expected: device, received: ' + data.type);
      }

      return data;
    },

    async list(
      limit: number,
      page: number,
      filter?: string
    ): Promise<TEntityListResponse<'device', TDeviceSummaryResBody> | undefined> {
      let data;
      if (filter) {
        data = await apiClient.get<TEntityListResponse<'device', TDeviceSummaryResBody>>(
          `/devices?limit=${limit}&page=${page}&${filter}`
        );
      } else {
        data = await apiClient.get<TEntityListResponse<'device', TDeviceSummaryResBody>>(
          `/devices?limit=${limit}&page=${page}`
        );
      }

      if (data && data.type !== 'device') {
        throw new Error('Invalid entity type received, expected: device, received: ' + data.type);
      }

      return data;
    },

    async list_by_status(
      limit: number,
      page: number,
      status: 'OFFLINE' | 'ONLINE' | 'ERROR' | 'NEVER_SEEN',
      filter?: string
    ): Promise<TEntityListResponse<'device', TDeviceSummaryResBody> | undefined> {
      let data;
      if (filter) {
        data = await apiClient.get<TEntityListResponse<'device', TDeviceSummaryResBody>>(
          `/devices?limit=${limit}&page=${page}&filter[status]=${status}&${filter}`
        );
      } else {
        data = await apiClient.get<TEntityListResponse<'device', TDeviceSummaryResBody>>(
          `/devices?limit=${limit}&page=${page}&filter[status]=${status}`
        );
      }

      if (data && data.type !== 'device') {
        throw new Error('Invalid entity type received, expected: device, received: ' + data.type);
      }

      return data;
    },

    async list_area_devices(areaId: string): Promise<TEntityListResponse<'device', TDeviceSummaryResBody> | undefined> {
      const data = await apiClient.get<TEntityListResponse<'device', TDeviceSummaryResBody>>(
        `/areas/${areaId}/devices`
      );

      if (data && data.type !== 'device') {
        throw new Error('Invalid entity type received, expected: device, received: ' + data.type);
      }

      return data;
    },

    async list_actions(
      limit: number,
      page: number,
      deviceId: string
    ): Promise<TEntityListResponse<'device-action', TDeviceActionSummaryResBody> | undefined> {
      const data = await apiClient.get<TEntityListResponse<'device-action', TDeviceActionSummaryResBody>>(
        `/devices/${deviceId}/actions?sort[created_at]=desc&limit=${limit}&page=${page}`
      );

      if (data && data.type !== 'device-action') {
        throw new Error('Invalid entity type received, expected: device-action, received: ' + data.type);
      }

      return data;
    },

    async list_errors(
      limit: number,
      page: number,
      deviceId: string
    ): Promise<TEntityListResponse<'device-error', TDeviceErrorSummaryResBody> | undefined> {
      const data = await apiClient.get<TEntityListResponse<'device-error', TDeviceErrorSummaryResBody>>(
        `/devices/${deviceId}/errors?sort[created_at]=desc&limit=${limit}&page=${page}`
      );

      if (data && data.type !== 'device-error') {
        throw new Error('Invalid entity type received, expected: device-error, received: ' + data.type);
      }

      return data;
    },

    async list_metrics(
      limit: number,
      page: number,
      deviceId: string
    ): Promise<TEntityListResponse<'device-metric', TDeviceMetricSummaryResBody> | undefined> {
      const data = await apiClient.get<TEntityListResponse<'device-metric', TDeviceMetricSummaryResBody>>(
        `/devices/${deviceId}/metrics?limit=${limit}&page=${page}`
      );

      if (data && data.type !== 'device-metric') {
        throw new Error('Invalid entity type received, expected: device-metric, received: ' + data.type);
      }

      return data;
    },

    async get_metrics_for_text(
      deviceId: string,
      metricId: string
    ): Promise<TEntityListResponse<'device-metric', TDeviceMetricSummaryResBody> | undefined> {
      const data = await apiClient.get<TEntityListResponse<'device-metric', TDeviceMetricSummaryResBody>>(
        `/devices/${deviceId}/metrics?filter[device_profile_metric_id]=${metricId}&sort[created_at]=desc&limit=1`
      );

      if (data && data.type !== 'device-metric') {
        throw new Error('Invalid entity type received, expected: device-metric, received: ' + data.type);
      }

      return data;
    },

    async get_metrics_for_graph(
      deviceId: string,
      metricKey: string,
      timeFrame: 'day' | 'week' | 'month' | 'year'
    ): Promise<any | undefined> {
      return apiClient.get<any>(`/devices/${deviceId}/metrics/chart/${metricKey}/${timeFrame}`);
    },

    async get_states_for_graph(
      deviceId: string,
      stateKey: string,
      timeFrame: 'day' | 'week' | 'month' | 'year'
    ): Promise<any | undefined> {
      return apiClient.get<any>(`/devices/${deviceId}/states/chart/${stateKey}/${timeFrame}`);
    },

    async list_states(
      limit: number,
      page: number,
      deviceId: string
    ): Promise<TEntityListResponse<'device-state', TDeviceStateSummaryResBody> | undefined> {
      const data = await apiClient.get<TEntityListResponse<'device-state', TDeviceStateSummaryResBody>>(
        `/devices/${deviceId}/states?limit=${limit}&page=${page}`
      );

      if (data && data.type !== 'device-state') {
        throw new Error('Invalid entity type received, expected: device-state, received: ' + data.type);
      }

      return data;
    },

    async get_frames_for_device(
      limit: number,
      page: number,
      deviceId: string,
      sort: 'asc' | 'desc'
    ): Promise<TEntityListResponse<'device-frame', TDeviceFrameSummaryResBody> | undefined> {
      const data = await apiClient.get<TEntityListResponse<'device-frame', TDeviceFrameSummaryResBody>>(
        `/devices/${deviceId}/frames?sort[created_at]=${sort}&limit=${limit}&page=${page}`
      );

      if (data && data.type !== 'device-frame') {
        throw new Error('Invalid entity type received, expected: device-frame, received: ' + data.type);
      }

      return data;
    },

    async get_last_state(
      limit: number,
      page: number,
      deviceId: string,
      stateTypeId: string,
      sort: 'asc' | 'desc'
    ): Promise<TEntityListResponse<'device-state', TDeviceStateSummaryResBody> | undefined> {
      const data = await apiClient.get<TEntityListResponse<'device-state', TDeviceStateSummaryResBody>>(
        `/devices/${deviceId}/states?sort[created_at]=${sort}&filter[device_profile_state_id]=${stateTypeId}&filter[flow_direction]=REPORTED&limit=${limit}&page=${page}`
      );

      if (data && data.type !== 'device-state') {
        throw new Error('Invalid entity type received, expected: device-state, received: ' + data.type);
      }

      return data;
    },

    async get_last_config(
      limit: number,
      page: number,
      deviceId: string,
      configTypeId: string,
      sort: 'asc' | 'desc',
      filter: 'REPORTED' | 'REQUESTED'
    ): Promise<TEntityListResponse<'device-config', TDeviceConfigSummaryResBody> | undefined> {
      const data = await apiClient.get<TEntityListResponse<'device-config', TDeviceConfigSummaryResBody>>(
        `/devices/${deviceId}/configs?sort[created_at]=${sort}&filter[device_profile_config_id]=${configTypeId}&filter[flow_direction]=${filter}&limit=${limit}&page=${page}`
      );

      if (data && data.type !== 'device-config') {
        throw new Error('Invalid entity type received, expected: device-config, received: ' + data.type);
      }

      return data;
    },

    async get_available_commands(id: string): Promise<TEntityListResponse<'device-command', any> | undefined> {
      const data = await apiClient.get<TEntityListResponse<'device-command', any>>(`/devices/${id}/commands`);

      if (data && data.type !== 'device-command') {
        throw new Error('Invalid entity type received, expected: device-command, received: ' + data.type);
      }

      return data;
    },

    async execute_command(id: string, command: any): Promise<void> {
      return apiClient.post(`/devices/${id}/commands`, command);
    },

    async set_group(id: string, body: { subgroup_id: string | null }): Promise<void> {
      return apiClient.put(`/devices/${id}/group`, body);
    },

    async metrics_chart_data(
      key: 'power',
      timeFrame: 'day' | 'week' | 'month' | 'year',
      type: 'group' | 'company' | 'area' | 'all',
      value: string
    ): Promise<TEntityResponse<'chart-data', TChartData> | undefined> {
      let data;
      if (type === 'all') {
        data = await apiClient.get<TEntityResponse<'chart-data', TChartData>>(
          `/device-metrics/chart/${key}/${timeFrame}`
        );
      } else {
        let filter = 'subgroup_id';

        if (type === 'group') {
          filter = 'subgroup_id';
        } else if (type === 'company') {
          filter = 'company_id';
        } else {
          filter = 'area_id';
        }

        data = await apiClient.get<TEntityResponse<'chart-data', TChartData>>(
          `/device-metrics/chart/${key}/${timeFrame}?${filter}=${value}`
        );
      }

      if (data && data.type !== 'chart-data') {
        throw new Error('Invalid entity type received, expected: chart-data, received: ' + data.type);
      }

      return data;
    },

    async activate(id: string, body?: TActivateDeviceBody): Promise<void> {
      return apiClient.post(`/devices/${id}/activate`, body);
    },

    async reactivate(id: string): Promise<void> {
      return apiClient.post(`/devices/${id}/reactivate`);
    },

    async import_from_google_sheet(
      sheetTitle: string
    ): Promise<TEntityResponse<'device-import', TDeviceImportResBody> | undefined> {
      const data = await apiClient.post<{ title: string }, TEntityResponse<'device-import', TDeviceImportResBody>>(
        `/devices/imports`,
        {
          title: sheetTitle,
        }
      );

      if (data && data.type !== 'device-import') {
        throw new Error('Invalid entity type received, expected: device-import, received: ' + data.type);
      }

      return data;
    },

    async export_devices(): Promise<TEntityResponse<'device-export', TDeviceExportResBody> | undefined> {
      const data = await apiClient.post<void, TEntityResponse<'device-export', TDeviceExportResBody>>(
        `/devices/exports`
      );

      if (data && data.type !== 'device-export') {
        throw new Error('Invalid entity type received, expected: device-export, received: ' + data.type);
      }

      return data;
    },

    async upload_import_xml(file: File): Promise<TEntityResponse<'device-import', TDeviceImportResBody> | undefined> {
      const formData = new FormData();
      formData.append('file', file);

      const data = await apiClient.post<FormData, TEntityResponse<'device-import', TDeviceImportResBody>>(
        `/devices/imports/upload`,
        formData
      );

      if (data && data.type !== 'device-import') {
        throw new Error('Invalid entity type received, expected: device-import, received: ' + data.type);
      }

      console.log(data);

      return data;
    },

    async process_device_import(
      importId: string
    ): Promise<TEntityResponse<'device-import', TDeviceImportResBody> | undefined> {
      const data = await apiClient.post<void, TEntityResponse<'device-import', TDeviceImportResBody>>(
        `/devices/imports/${importId}/process`
      );

      console.log('process_device_import data:', data);

      /*if (data && data.type !== 'device-import') {
        throw new Error('Invalid entity type received, expected: device-import, received: ' + data.type);
      }*/

      return data;
    },

    async confirm_device_import(
      importId: string
    ): Promise<TEntityResponse<'device-import', TDeviceImportResBody> | undefined> {
      const data = await apiClient.post<void, TEntityResponse<'device-import', TDeviceImportResBody>>(
        `/devices/imports/${importId}/confirm`
      );

      if (data && data.type !== 'device-import') {
        throw new Error('Invalid entity type received, expected: device-import, received: ' + data.type);
      }

      return data;
    },

    async list_exports(limit: number, page: number): Promise<TEntityListResponse<'device-export', any> | undefined> {
      const data = await apiClient.get<TEntityListResponse<'device-export', any>>(
        `/devices/exports?limit=${limit}&page=${page}&sort[created_at]=desc`
      );

      if (data && data.type !== 'device-export') {
        throw new Error('Invalid entity type received, expected: device-export, received: ' + data.type);
      }

      return data;
    },

    async list_imports(limit: number, page: number): Promise<TEntityListResponse<'device-import', any> | undefined> {
      const data = await apiClient.get<TEntityListResponse<'device-import', any>>(
        `/devices/imports?limit=${limit}&page=${page}&sort[created_at]=desc`
      );

      if (data && data.type !== 'device-import') {
        throw new Error('Invalid entity type received, expected: device-import, received: ' + data.type);
      }

      return data;
    },

    async get_import(id: string): Promise<TEntityResponse<'device-import', TDeviceImportResBody> | undefined> {
      const data = await apiClient.get<TEntityResponse<'device-import', TDeviceImportResBody>>(
        `/devices/imports/${id}`
      );

      if (data && data.type !== 'device-import') {
        throw new Error('Invalid entity type received, expected: device-import, received: ' + data.type);
      }

      return data;
    },

    async get_export(id: string): Promise<TEntityResponse<'device-export', TDeviceExportResBody> | undefined> {
      const data = await apiClient.get<TEntityResponse<'device-export', TDeviceExportResBody>>(
        `/devices/exports/${id}`
      );

      if (data && data.type !== 'device-export') {
        throw new Error('Invalid entity type received, expected: device-export, received: ' + data.type);
      }

      return data;
    },

    async stream_export(id: string, onEvent: TSSEHandler, signal: AbortSignal): Promise<void> {
      return apiClient.stream(`/devices/exports/${id}/stream`, onEvent, signal);
    },

    async stream_import(id: string, onEvent: TSSEHandler, signal: AbortSignal): Promise<void> {
      return apiClient.stream(`/devices/imports/${id}/stream`, onEvent, signal);
    },

    async delete_import(id: string): Promise<void> {
      return await apiClient.delete(`/devices/imports/${id}`);
    },

    async delete_export(id: string): Promise<void> {
      return await apiClient.delete(`/devices/exports/${id}`);
    },
  };
}

export { TDevicesClient, DevicesClient, TChartData };
